import { useEffect, useRef, useState } from "react";
import { type ImageCache, imageCache } from "./ImageCache";
import { MAX_THUMBNAIL_RETRIES } from "./constants";
import type { ImageCacheSnapshot } from "./types";

export { MAX_THUMBNAIL_RETRIES };

/**
 * 첫 retry backoff 의 base 딜레이 (ms).
 *
 * 이후 매 시도마다 2배로 늘어나며 `MAX_RETRY_DELAY_MS` 에서 상한.
 * 시퀀스: 1000 → 2000 → 3000 (clamped)
 */
export const BASE_RETRY_DELAY_MS = 1000;
export const MAX_RETRY_DELAY_MS = 3000;

export interface UseBackoffImageLoaderOptions {
  /** false 인 동안에는 캐시를 acquire 하지 않는다. 기본값 true. */
  enabled?: boolean;
  /** 이 소스를 HEIC 로 다뤄 변환 경로로 보낼지. 미지정 시 확장자로 추정. */
  isHeic?: boolean;
  /**
   * error 발생 시 지수 백오프로 재시도할지.
   *
   * 비동기 생성되는 server thumbnail 처럼 "곧 생길" URL 에만 true. 원본 src 의 error 는
   * 재시도 의미가 없으므로 false 로 두어 즉시 error 로 노출한다.
   */
  retryOnError?: boolean;
  /** retryOnError=true 일 때 최대 재시도 횟수. 기본 {@link MAX_THUMBNAIL_RETRIES}. */
  maxRetries?: number;
  /** 재시도를 모두 소진했을 때 1회 호출된다. 상위가 다른 소스로 낙착하는 신호로 사용. */
  onExhausted?: () => void;
  /** 사용할 캐시 인스턴스. 기본은 모듈 싱글톤. */
  cache?: ImageCache;
}

export interface UseBackoffImageLoaderResult {
  /** 표시 가능한 URL (status === "ready" 일 때만 non-null). */
  src: string | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * 단일 URL 을 캐시에서 로드하되, error 시 지수 백오프로 재시도하고 소진 시 상위에 통보하는 훅.
 *
 * 책임은 "주어진 하나의 URL 을 끈질기게 로드" 로 한정된다. "어떤 URL 을 고를지" 는 상위가
 * (예: {@link selectImageSource}) 순수 함수로 결정해 `url` 로 넘긴다.
 *
 * 재시도 메커니즘:
 * 1. url 로 cache 구독 시작.
 * 2. cache 가 error 를 보고하고 retryOnError 면 지수 백오프 setTimeout 예약.
 * 3. setTimeout 발화 시 retryKey 증가 → cache.acquire 재호출 → ImageCache 자체 retry.
 * 4. maxRetries 소진 시 onExhausted() 1회 호출.
 *
 * url 이 바뀌면 모든 재시도 상태가 render 단계에서 동기 리셋된다. useEffect 리셋은 비동기라
 * cache 콜백이 effect 보다 먼저 발화하면 stale 카운트로 premature 소진이 발생할 수 있다.
 */
export function useBackoffImageLoader(
  url: string | null,
  options?: UseBackoffImageLoaderOptions,
): UseBackoffImageLoaderResult {
  const {
    enabled = true,
    isHeic,
    retryOnError = false,
    maxRetries = MAX_THUMBNAIL_RETRIES,
    onExhausted,
    cache = imageCache,
  } = options ?? {};

  const [retryKey, setRetryKey] = useState(0);
  const attemptsRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onExhaustedRef = useRef(onExhausted);
  onExhaustedRef.current = onExhausted;

  const active = enabled && url !== null;

  const [snapshot, setSnapshot] = useState<ImageCacheSnapshot | null>(() => {
    if (!active) return null;
    const peek = cache.peek(url as string, { isHeic });
    return peek?.status === "error" ? null : peek;
  });

  // url 변경 시 render 단계에서 동기 리셋 (stale snapshot 무효화 + 재시도 카운터/타이머 정리).
  const [trackedUrl, setTrackedUrl] = useState(url);
  if (trackedUrl !== url) {
    setTrackedUrl(url);
    setRetryKey(0);
    attemptsRef.current = 0;
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (active) {
      const peek = cache.peek(url as string, { isHeic });
      setSnapshot(peek?.status === "error" ? null : peek);
    } else {
      setSnapshot(null);
    }
  }

  // 언마운트 시 예약된 timer 정리.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  // 캐시 구독 + url / retryKey 변경 시 재 acquire.
  // biome-ignore lint/correctness/useExhaustiveDependencies: retryKey 는 본문에서 읽히지 않지만 의도적으로 dep 에 둔다 — 증가 시 release-then-acquire 가 다시 수행되어 ImageCache 가 retry 시그널로 받는다
  useEffect(() => {
    if (!active) return;
    const release = cache.acquire(url as string, (snap) => setSnapshot(snap), { isHeic });
    return release;
  }, [url, active, isHeic, cache, retryKey]);

  // 캐시 error 발생 시 backoff 로 retry 예약 (retryOnError 경로에서만).
  useEffect(() => {
    if (!active || !snapshot || snapshot.status !== "error") return;
    if (!retryOnError) return;

    if (attemptsRef.current >= maxRetries) {
      onExhaustedRef.current?.();
      return;
    }
    const attempt = attemptsRef.current;
    attemptsRef.current = attempt + 1;
    const delay = Math.min(BASE_RETRY_DELAY_MS * 2 ** attempt, MAX_RETRY_DELAY_MS);

    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setRetryKey((k) => k + 1);
    }, delay);
  }, [snapshot, retryOnError, active, maxRetries]);

  if (!active) {
    return { src: null, isLoading: false, error: null };
  }

  if (!snapshot) {
    return { src: null, isLoading: true, error: null };
  }

  if (snapshot.status === "ready") {
    return { src: snapshot.url ?? null, isLoading: false, error: null };
  }

  if (snapshot.status === "error") {
    // retryOnError 면 재시도 진행 중 (또는 곧 소진 → 상위가 낙착) 이므로 loading 으로 노출.
    if (retryOnError) {
      return { src: null, isLoading: true, error: null };
    }
    return { src: null, isLoading: false, error: snapshot.error ?? new Error("Image load failed") };
  }

  return { src: null, isLoading: true, error: null };
}
